import { useState, useEffect } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Shield, Search, AlertCircle, XCircle, FileText, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';
import { useSeoMeta } from '@/hooks/useSeoMeta';
import api, { API_BASE } from '@/lib/api';

export default function VerifyPage() {
  const [params] = useSearchParams();
  const routeParams = useParams();  // /verify/:code path form
  const navigate = useNavigate();
  const initialCode = routeParams.code || params.get('code') || '';
  const [code, setCode] = useState(initialCode);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useSeoMeta({ seo_title: 'Verify a Certificate - The Fashion Detective' });

  // Auto-verify if code in URL. Legacy ?code=X → redirect to /verify/X so the
  // canonical path form always shows in the address bar.
  useEffect(() => {
    if (routeParams.code) {
      setCode(routeParams.code);
      doVerify(routeParams.code);
      return;
    }
    const urlCode = params.get('code');
    if (urlCode) {
      navigate(`/verify/${encodeURIComponent(urlCode)}`, { replace: true });
    }
  }, [routeParams.code]); // eslint-disable-line

  const doVerify = async (verifyCode) => {
    const c = (verifyCode || code).trim();
    if (!c) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setSearched(true);
    try {
      const res = await api.get(`/verify/${c}`);
      setResult(res.data);
    } catch (err) {
      setError(err.response?.status === 404
        ? 'No certificate found. Check the certificate code or order number and try again.'
        : 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const c = code.trim();
    if (!c) return;
    // Push the canonical path form into the URL so the address bar and the
    // browser history reflect what the user searched for.
    if (c !== routeParams.code) {
      navigate(`/verify/${encodeURIComponent(c)}`);
    } else {
      doVerify(c);
    }
  };

  return (
    <div data-testid="verify-page">
      {/* Hero */}
      <section style={{ background: 'linear-gradient(135deg, hsl(36 33% 98%) 0%, hsl(36 22% 94%) 55%, hsl(35 19% 88%) 100%)' }}>
        <div className="max-w-xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center">
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
            <Shield className="h-10 w-10 mx-auto mb-4 text-ink" strokeWidth={1.5} />
            <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-[-0.02em] text-ink mb-3">Verify a Certificate</h1>
            <p className="text-sm text-muted-foreground mb-8">Enter a certificate code or order number to verify authenticity.</p>
            <form onSubmit={handleSubmit} className="flex gap-2" data-testid="verify-form">
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Certificate code or order number" className="flex-1 font-mono text-sm bg-white h-11" data-testid="verify-code-input" />
              <Button type="submit" disabled={loading || !code.trim()} className="gap-1.5 rounded-[12px] h-11 px-6" data-testid="verify-certificate-submit-button">
                {loading ? <span className="animate-pulse">Checking...</span> : <><Search className="h-4 w-4" /> Verify</>}
              </Button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Result */}
      <section className="py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          {result && result.multiple && (
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
              <Card className="bg-white border-stone/50 shadow-[var(--shadow-card)]" data-testid="verify-multiple-results">
                <CardContent className="p-6">
                  <p className="text-sm text-ink font-medium mb-1">Order {result.order_number} has {result.certificates.length} certificates</p>
                  <p className="text-xs text-muted-foreground mb-4">Select an item to view its certificate.</p>
                  <div className="space-y-2">
                    {result.certificates.map((c, i) => (
                      <button key={i} type="button"
                        className="w-full flex items-center gap-3 p-3 rounded-lg border border-stone/40 bg-white hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors text-left cursor-pointer"
                        data-testid={`verify-multi-cert-${i}`}
                        onClick={() => navigate(`/verify/${encodeURIComponent(c.certificate_code)}`)}>
                        <Shield className="h-4 w-4 flex-shrink-0" style={{ color: c.is_revoked ? '#5A1F2A' : '#0E6B5D' }} />
                        <span className="text-sm text-ink flex-1 truncate">{c.item_title || 'Item'}{c.brand_name ? ` — ${c.brand_name}` : ''}</span>
                        <span className="font-mono text-xs text-muted-foreground uppercase">{c.certificate_code}</span>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {result && !result.multiple && (
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
              <Card className="bg-white border-stone/50 shadow-[var(--shadow-card)] overflow-hidden">
                {/* Status banner - NO generic status on valid certs */}
                <div className={`px-6 py-3 flex items-center gap-2 ${result.is_revoked ? 'bg-[rgba(90,31,42,0.08)]' : 'bg-[rgba(14,107,93,0.06)]'}`}>
                  {result.is_revoked
                    ? <XCircle className="h-5 w-5" style={{ color: '#5A1F2A' }} />
                    : <Shield className="h-5 w-5" style={{ color: '#0E6B5D' }} />
                  }
                  <span className="text-sm font-medium" style={{ color: result.is_revoked ? '#5A1F2A' : '#0E6B5D' }}>
                    {result.is_revoked ? 'This Certificate Has Been Revoked' : 'Certificate Verified'}
                  </span>
                  {result.is_revoked && (
                    <Badge className="ml-auto text-[10px]" style={{ background: 'rgba(90,31,42,0.12)', color: '#5A1F2A' }} data-testid="certificate-status-badge">
                      Revoked
                    </Badge>
                  )}
                </div>

                <CardContent className="p-6">
                  {/* Certificate details */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground uppercase tracking-wider">Certificate Code</span>
                      <span className="font-mono text-sm text-ink font-medium" data-font="mono">{result.certificate_code}</span>
                    </div>
                    <Separator />
                    {result.item_title && (
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-muted-foreground">Item</span>
                        <span className="text-sm text-ink font-medium">{result.item_title}</span>
                      </div>
                    )}
                    {result.brand_name && (
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-muted-foreground">Brand</span>
                        <span className="text-sm text-ink">{result.brand_name}</span>
                      </div>
                    )}
                    {result.category_name && (
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-muted-foreground">Category</span>
                        <span className="text-sm text-ink">{result.category_name}</span>
                      </div>
                    )}
                    {result.determination_date && (
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-muted-foreground">Determination Date</span>
                        <span className="text-sm text-ink">{new Date(result.determination_date).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>

                  {/* The certificate document itself (offline uploads show the
                      original PDF; website orders show the issued certificate).
                      Hidden for revoked certificates. */}
                  {result.certificate_pdf_url && !result.is_revoked && (
                    <div className="mt-5 pt-4 border-t border-stone/30" data-testid="verify-certificate-document">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-xs text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                          <FileText className="h-3.5 w-3.5" /> Certificate
                        </p>
                        <div className="flex items-center gap-3">
                          <a href={`${API_BASE}/verify/${encodeURIComponent(result.certificate_code)}/certificate.pdf`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-white" style={{ backgroundColor: '#1F2937' }} data-testid="verify-certificate-download-btn">
                            <FileText className="h-3.5 w-3.5" /> Download certificate PDF
                          </a>
                          <a href={result.certificate_pdf_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: '#1F5E7A' }} data-testid="verify-certificate-pdf-open">
                            Open full size <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      </div>
                      <div className="rounded-lg border border-stone/30 overflow-hidden bg-stone/5">
                        <object
                          data={`${result.certificate_pdf_url}#toolbar=0&navpanes=0`}
                          type="application/pdf"
                          aria-label="Certificate of Authenticity"
                          className="w-full h-[520px] bg-white"
                          data-testid="verify-certificate-pdf-frame"
                        >
                          <div className="p-10 text-center">
                            <FileText className="h-8 w-8 mx-auto mb-3 text-muted-foreground/40" />
                            <p className="text-sm text-muted-foreground mb-4">Your browser can't preview PDFs inline.</p>
                            <a href={result.certificate_pdf_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium text-white" style={{ backgroundColor: '#1F2937' }} data-testid="verify-certificate-pdf-fallback">
                              View Certificate (PDF) <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          </div>
                        </object>
                      </div>
                    </div>
                  )}

                  {/* Selected certificate images — click a thumbnail to view full size */}
                  {result.certificate_images && result.certificate_images.length > 0 && (
                    <div className="mt-5 pt-4 border-t border-stone/30">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Certificate Images</p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {result.certificate_images.map((img, i) => {
                          const url = img.url || `${API_BASE}/storage/files/${img.storage_key}`;
                          return (
                            <button
                              key={i}
                              type="button"
                              onClick={() => setLightbox({ url, label: img.slot_key || `Image ${i + 1}`, rotation: img.rotation || 0 })}
                              className="aspect-square rounded-lg overflow-hidden border border-stone/30 bg-stone/5 cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-ring/40 transition-transform hover:scale-[1.02]"
                              data-testid={`verify-image-thumb-${i}`}
                            >
                              <img src={url} alt={img.slot_key || `Image ${i + 1}`} className="w-full h-full object-cover" style={img.rotation ? { transform: `rotate(${img.rotation}deg)` } : undefined} />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <p className="text-xs text-muted-foreground text-center mt-6">
                This certificate was issued by The Fashion Detective. For questions, contact support@thefashiondetective.com
              </p>
            </motion.div>
          )}

          {error && searched && (
            <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}>
              <Alert className="bg-white border-stone/50" data-testid="verify-error">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-sm">{error}<br /><span className="text-xs text-muted-foreground">Contact support@thefashiondetective.com for help.</span></AlertDescription>
              </Alert>
            </motion.div>
          )}

          {!result && !error && !loading && (
            <div className="text-center text-xs text-muted-foreground">
              <p>Certificate codes are found on authentic item certificates and in determination emails.</p>
            </div>
          )}
        </div>
      </section>

      {/* Full-size image lightbox */}
      <Dialog open={!!lightbox} onOpenChange={(open) => !open && setLightbox(null)}>
        <DialogContent className="max-w-4xl p-2 bg-white" data-testid="verify-image-lightbox">
          {lightbox && (
            <img src={lightbox.url} alt={lightbox.label} className="w-full max-h-[80vh] object-contain rounded" style={lightbox.rotation ? { transform: `rotate(${lightbox.rotation}deg)` } : undefined} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
